import { useFormHook, useSetError } from '@Hooks/index';
import {
  LiliNotification,
  LiliNotificationType,
  LiliToastDefaultConfig,
} from '@lili/lili-frontend-common';
import { useNavigationManager } from '@Router/useNavigationManager';
import { OnboardingFormFields } from '@Types/index';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { useSendMfaCode, useValidateAuthorizedUserMfa } from '../api/verifyPhone.api';
import { useBoundStore } from '@Store/useBoundStore';
import { useCountdownTimer } from '@Hooks/useTimer';
import { useEffect } from 'react';

export const phoneFormScheme = z.object({
  [OnboardingFormFields.PHONE_OTP]: z.string(),
});

type PhoneForm = z.infer<typeof phoneFormScheme>;

export const useVerifyPhoneLogic = () => {
  const { navigateToNextPage } = useNavigationManager();
  const { mutateAsync: validateMfa, isPending: isLoading } = useValidateAuthorizedUserMfa();
  const { mutateAsync: sendMfaCode, isPending: isSendingMfaCode } = useSendMfaCode();
  const { t } = useTranslation(['inviteTeamMember']);
  const setAccessToken = useBoundStore.getState().setAuthUserAccessToken;

  const { startTimer, timeLeft } = useCountdownTimer({
    intervalDuration: 30,
    onFinishCountdown: () => {},
  });

  useEffect(() => {
    startTimer();
  }, []);

  const handleSendMfaCode = async () => {
    await sendMfaCode();
  };

  const defaultValues: PhoneForm = {
    [OnboardingFormFields.PHONE_OTP]: '',
  };

  const formMethods = useFormHook({
    formSchema: phoneFormScheme,
    formDefaultValues: defaultValues,
    schemaTitle: 'otp-phone-form',
    reValidateMode: 'onBlur',
  });

  const { isValid } = useFormState({ control: formMethods.control });

  const handleVerifyCode = async (code: string) => {
    const response = await validateMfa({
      answer: code,
    });
    setAccessToken(response.accessToken);
    navigateToNextPage();
  };

  const handleResendCode = async () => {
    try {
      await handleSendMfaCode();
      startTimer();
      toast(
        <LiliNotification
          type={LiliNotificationType.SUCCESS}
          text={t('verifyPhone.resendCodeSuccess')}
          id='resend-otp-success'
        />,
        { ...LiliToastDefaultConfig }
      );
    } catch {
      toast(
        <LiliNotification
          type={LiliNotificationType.ERROR}
          text={t('verifyPhone.resendCodeError')}
          id='resend-otp-failure'
        />,
        { ...LiliToastDefaultConfig }
      );
    }
  };

  useSetError({ formMethods });

  return {
    handleVerifyCode,
    handleResendCode,
    isLoading,
    isSendingMfaCode,
    isValid,
    formMethods,
    handleSendMfaCode,
    timeLeft,
  };
};
